import dayjs from 'dayjs';
import { isEqual } from 'lodash';

import { BomPartType } from 'bom/enums';
import { BomPartHelper } from 'bom/helpers';
import { IPart } from 'parts/types';
import { PricingSourceType } from 'pricing/enums';
import { PricingHelper } from 'pricing/helpers';
import { PriceBasedOn, PricingStrategy } from 'quotes/enums';
import { QuotePartHelper, QuotePartPricingHelper } from 'quotes/helpers';
import { ICustomerQuotePart, IQuotePart, IQuotePartLineForm } from 'quotes/types';
import { getCostValidityDefaults } from 'quotes/utils';

export const getLineDetailsDefaultValues = (
  quotePart: IQuotePart,
  defaultValues: IQuotePartLineForm,
  quotePricingDate?: string,
  quotePricingStrategy?: PricingStrategy,
  isAlternateVersionEnabled = false
): IQuotePartLineForm => {
  const customerQuotePart = quotePart as ICustomerQuotePart;

  const part = quotePart?.partArr as IPart;
  const manufacturer = BomPartHelper.getManufacturer(quotePart);
  // The form resets to these defaults on open, so the pick must be seeded, not left alone.
  const selectedSpa = QuotePartPricingHelper.mapQuotePartToSelectedSpa(customerQuotePart);

  return {
    part: part
      ? {
          id: part?.id,
          number: QuotePartHelper.getLinePartNumber(quotePart),
          verified: Boolean(part?.verified),
          familyId: part?.familyId,
          description: part?.description,
          descriptionLong: part?.descriptionLong,
          uoms: quotePart?.allowedUoms,
          manufacturerId: manufacturer?.id,
          manufacturerName: manufacturer?.name,
          minimumQuantityInQuoteUom: quotePart?.corporatePartConfig?.minimumQuantityInQuoteUom,
          orderIncrementInQuoteUom: quotePart?.corporatePartConfig?.orderIncrementInQuoteUom,
          externalReplacementCost: quotePart?.externalReplacementCost,
          externalRepCostRegistrationNum: quotePart?.externalRepCostRegistrationNum,
          supplier: quotePart?.supplier,
          shipmentType: customerQuotePart?.shipmentType,
          externalPartStatus: quotePart?.externalPartStatus,
          marketingMemo: quotePart?.marketingMemo,
          alternateVendorPartNumber: quotePart?.alternateVendorPartNumber,
        }
      : null,
    manufacturer,
    description:
      quotePart?.type === BomPartType.COMPONENT
        ? quotePart?.componentName
        : part?.descriptionLong || part?.description || '',
    quantity: quotePart?.quantity || null,
    uom: quotePart?.uom || null,
    standardCost: quotePart?.computedCost || null,
    purchaseCost: quotePart?.computedPurchaseCost || null,
    costSource: {
      vendorQuoteNumber: quotePart?.id
        ? QuotePartHelper.getVendorQuoteNumber(customerQuotePart)
        : null,
      ...getCostValidityDefaults(quotePart?.id ? customerQuotePart : null),
    },
    price: quotePart?.price ?? null,
    isReplacementCostCustom:
      !quotePart?.externalReplacementCost ||
      (!!quotePart?.replacementCost &&
        quotePart?.externalReplacementCost !== quotePart?.replacementCost),
    selectedSpa,
    // A selected-SPA line is never manually costed, whatever the rest of the row says.
    isCostingCustom:
      !selectedSpa &&
      (PricingSourceType.CUSTOM === customerQuotePart?.costSourceType ||
        !quotePart?.computedPurchaseCost),
    prioritizeLegacyCost: !!customerQuotePart?.prioritizeLegacyCost,
    replacementCost: quotePart?.replacementCost ?? quotePart?.externalReplacementCost,
    replacementCostNumber:
      quotePart?.replacementCostNumber || quotePart?.externalRepCostRegistrationNum,
    shipmentType: customerQuotePart?.shipmentType,
    corporateLocation: customerQuotePart?.corporateLocation,
    companyManufacturerName: customerQuotePart?.companyManufacturerName || null,
    companyManufacturerId: customerQuotePart?.companyManufacturerId || null,
    accountsPayableId: customerQuotePart?.accountsPayableId || null,
    pricingDate: getPricingDateDefault(
      quotePart?.pricingDate,
      quotePart?.id ? undefined : quotePricingDate
    ),
    pricingStrategy: isAlternateVersionEnabled
      ? getPricingStrategyDefault(
          quotePart?.pricingStrategy,
          quotePart?.id ? undefined : quotePricingStrategy
        )
      : undefined,
    priceBasedOn: (customerQuotePart as ICustomerQuotePart)?.priceBasedOn || PriceBasedOn.QUOTED,
    margin: PricingHelper.computeGpmPercentage(quotePart?.computedCost, quotePart?.price),
    pricingMargin: PricingHelper.computeGpmPercentage(
      customerQuotePart?.priceBasedOn === PriceBasedOn.REPLACEMENT
        ? quotePart?.replacementStandardCost ?? quotePart?.externalReplacementStandardCost
        : quotePart?.computedCost,
      quotePart?.price
    ),
    // Deferred lifecycle: only replacement-mode lines carry a trustworthy standard cost (the BE
    // maintains it at its need-points). In quoted mode the row may still hold a legacy/preserved
    // value — seed null so the drawer shows the deferred placeholder instead of a stale number.
    replacementStandardCost:
      (customerQuotePart as ICustomerQuotePart)?.priceBasedOn === PriceBasedOn.REPLACEMENT
        ? quotePart?.replacementStandardCost ?? quotePart?.externalReplacementStandardCost ?? null
        : null,
    ...defaultValues,
  };
};

const getPricingDateDefault = (linePricingDate?: string, quotePricingDate?: string) => {
  // Existing line: always show saved value (even if past — warning shown in UI)
  if (linePricingDate) {
    return dayjs(linePricingDate);
  }

  // New line: only inherit quote-level date if it's after today (tomorrow+)
  if (quotePricingDate && dayjs(quotePricingDate).isAfter(dayjs(), 'day')) {
    return dayjs(quotePricingDate);
  }

  return null;
};

const isKnownPricingStrategy = (value?: PricingStrategy): boolean =>
  !!value && Object.values(PricingStrategy).includes(value);

const getPricingStrategyDefault = (
  linePricingStrategy?: PricingStrategy,
  quotePricingStrategy?: PricingStrategy
): PricingStrategy => {
  if (isKnownPricingStrategy(linePricingStrategy)) {
    return linePricingStrategy;
  }

  if (isKnownPricingStrategy(quotePricingStrategy)) {
    return quotePricingStrategy;
  }

  return PricingStrategy.EXTERNAL;
};

// No baseline means the form never settled on the line: report a change so the caller saves.
export const hasLineFormChanges = (
  currentValues: Partial<IQuotePartLineForm>,
  initialValues: Partial<IQuotePartLineForm> | null
) => !initialValues || !isEqual(currentValues, initialValues);
